import { FC } from "react";
import { Checkbox } from "antd";
import { clsx } from "clsx";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { GenericCard } from "../../../../../common/components/GenericCard";
import { formatNumberWithSuffix } from "../../../../../common/Number";
import classes from "./QuarterCard.module.less";

const DealQuarterCard: FC<{ savings: number; contracts: number; contractsLabel: string }> = ({
    savings,
    contracts,
    contractsLabel,
}) => (
    <>
        <div className={classes.money}>${formatNumberWithSuffix(savings)}</div>
        <div className={classes.info}>In potential savings</div>
        <div className={classes.info}>
            {contracts} {contractsLabel}
        </div>
    </>
);

const RFXQuarterCard: FC<{ contracts: number; contractsLabel: string }> = ({
    contracts,
    contractsLabel,
}) => (
    <>
        <div className={classes.money}>{contracts}</div>
        <div className={classes.info}>{contractsLabel}</div>
    </>
);

const EmptyQuarterCard: FC = () => (
    <>
        <div className={classes.noData}>
            <ExclamationCircleOutlined />
        </div>
        <div className={classes.info}>Data not available</div>
    </>
);

export type QuarterCardType = "deal" | "rfx";

export interface QuarterCardProps {
    quarter?: number;
    savings?: number;
    contracts?: number;
    contractsLabel?: string;
    type?: QuarterCardType;
    selected?: boolean;
    checked?: boolean;
    showCheckbox?: boolean;
    message?: string;
    onSelect?: (quarter: number) => void;
    onToggle?: (quarter: number) => void;
}

export const QuarterCard: FC<QuarterCardProps> = ({
    quarter = 1,
    savings = 0,
    contracts = 0,
    contractsLabel = "contracts",
    type = "deal",
    selected,
    checked,
    showCheckbox,
    message,
    onSelect,
    onToggle,
}) => {
    if (message) {
        return (
            <GenericCard className={classes.container} type="info">
                <div className={classes.body}>
                    <div className={classes.noData}>
                        <ExclamationCircleOutlined />
                    </div>
                    <div className={classes.info}>{message}</div>
                </div>
                <div style={{ height: 20 }} />
            </GenericCard>
        );
    }

    const hasData = contracts > 0 || savings > 0;

    return (
        <GenericCard
            className={clsx(classes.container, selected && classes["container--selected"])}
            type={selected ? "success" : "info"}
            onClick={() => onSelect?.(quarter)}
        >
            <div className={classes.header}>
                <span className={classes.quarterLabel}>Q{quarter}</span>
                {showCheckbox && (
                    <Checkbox
                        checked={checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => onToggle?.(quarter)}
                    />
                )}
            </div>
            <div className={classes.body}>
                {!hasData ? (
                    <EmptyQuarterCard />
                ) : type === "deal" ? (
                    <DealQuarterCard
                        savings={savings}
                        contracts={contracts}
                        contractsLabel={contractsLabel}
                    />
                ) : (
                    <RFXQuarterCard contracts={contracts} contractsLabel={contractsLabel} />
                )}
            </div>
        </GenericCard>
    );
};
